import { useState, useEffect, useRef } from "react";
import { anatomyConstants } from "../../constants/anatomyConstants";
import { Roles } from "../../constants/roles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

/**
 * ModelGroupingModal
 *
 * Props:
 * - modelInfos: Array of model objects with {name, ...}
 * - existingGroups: Array of {name, indices, color}
 * - userRole: string (VIEWER, EDITOR, CREATOR, ADMIN)
 * - editMode: string ('personal', 'common', or null for new/override)
 * - onClose: () => void
 * - onSave: (groups: Array<{name, indices, color}>, applyToAllUsers: boolean) => void
 */
const ModelGroupingModal = ({
  modelInfos = [],
  existingGroups = [],
  userRole = Roles.VIEWER,
  editMode = null,
  onClose,
  onSave,
}) => {
  // Initialize groups: first column is "ungrouped", rest are named groups
  const [groups, setGroups] = useState([]);
  const [applyToAllUsers, setApplyToAllUsers] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null); // For click-to-transfer: {modelIndex, fromGroupIndex}
  const modalRef = useRef(null);

  useEffect(() => {
    // Initialize groups from existing data
    const groupedIndices = new Set();
    const initialGroups = [
      {
        id: "ungrouped",
        name: "Ungrouped",
        isUngrouped: true,
        indices: [],
        color: "#808080",
      },
    ];

    // Add existing groups
    existingGroups.forEach((group, idx) => {
      // Filter out invalid indices (edge case: model was deleted)
      const validIndices = (group.indices || []).filter(
        (i) => i >= 0 && i < modelInfos.length
      );

      // Only add group if it has valid indices or if we're editing
      if (validIndices.length > 0 || editMode) {
        initialGroups.push({
          id: `group-${idx}`,
          name: group.name || `Group ${idx + 1}`,
          isUngrouped: false,
          indices: validIndices,
          color: group.color || generateRandomColor(),
        });
        validIndices.forEach((i) => groupedIndices.add(i));
      }
    });

    // If no existing groups, add one empty group to start with
    if (existingGroups.length === 0) {
      initialGroups.push({
        id: `group-${Date.now()}`,
        name: "Group 1",
        isUngrouped: false,
        indices: [],
        color: generateRandomColor(),
      });
    }

    // Add ungrouped models
    modelInfos.forEach((_, idx) => {
      if (!groupedIndices.has(idx)) {
        initialGroups[0].indices.push(idx);
      }
    });

    setGroups(initialGroups);
  }, [modelInfos, existingGroups, editMode]);

  const generateRandomColor = () => {
    const colors = [
      "#8B4513",
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E2",
      "#F8B500",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const addNewGroup = () => {
    const newGroup = {
      id: `group-${Date.now()}`,
      name: `Group ${groups.length}`,
      isUngrouped: false,
      indices: [],
      color: generateRandomColor(),
    };
    setGroups([...groups, newGroup]);
  };

  const deleteGroup = (groupIndex) => {
    if (groupIndex === 0) return; // Can't delete ungrouped

    const groupToDelete = groups[groupIndex];
    const updatedGroups = groups.filter((_, idx) => idx !== groupIndex);

    // Move all models from deleted group to ungrouped
    updatedGroups[0].indices = [
      ...updatedGroups[0].indices,
      ...groupToDelete.indices,
    ];

    setGroups(updatedGroups);
  };

  const updateGroupName = (groupIndex, newName) => {
    setGroups(
      groups.map((g, idx) => (idx === groupIndex ? { ...g, name: newName } : g))
    );
  };

  const updateGroupColor = (groupIndex, newColor) => {
    setGroups(
      groups.map((g, idx) =>
        idx === groupIndex ? { ...g, color: newColor } : g
      )
    );
  };

  // Drag handlers for desktop
  const handleDragStart = (e, modelIndex, groupIndex) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedItem({ modelIndex, fromGroupIndex: groupIndex });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, toGroupIndex) => {
    e.preventDefault();
    if (!draggedItem) return;

    const { modelIndex, fromGroupIndex } = draggedItem;
    if (fromGroupIndex === toGroupIndex) {
      setDraggedItem(null);
      return;
    }

    // Remove from old group
    const updatedGroups = groups.map((g, idx) => {
      if (idx === fromGroupIndex) {
        return { ...g, indices: g.indices.filter((i) => i !== modelIndex) };
      }
      if (idx === toGroupIndex) {
        return { ...g, indices: [...g.indices, modelIndex] };
      }
      return g;
    });

    setGroups(updatedGroups);
    setDraggedItem(null);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e, modelIndex, groupIndex) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setDraggedItem({ modelIndex, fromGroupIndex: groupIndex });

    // Add visual feedback
    e.currentTarget.style.opacity = "0.5";
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!draggedItem || !touchStartPos) return;

    const touch = e.touches[0];

    // Find the element under touch point
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );
    const dropZone = elementBelow?.closest("[data-group-drop-zone]");

    if (dropZone) {
      // Visual feedback for drop zone
      document.querySelectorAll("[data-group-drop-zone]").forEach((el) => {
        el.classList.remove("bg-blue-100");
      });
      dropZone.classList.add("bg-blue-100");
    }
  };

  const handleTouchEnd = (e, originalGroupIndex) => {
    if (!draggedItem) return;

    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );
    const dropZone = elementBelow?.closest("[data-group-drop-zone]");

    // Remove visual feedback
    document.querySelectorAll("[data-group-drop-zone]").forEach((el) => {
      el.classList.remove("bg-blue-100");
    });

    if (e.currentTarget) {
      e.currentTarget.style.opacity = "1";
    }

    if (dropZone) {
      const toGroupIndex = parseInt(dropZone.getAttribute("data-group-index"));
      const { modelIndex, fromGroupIndex } = draggedItem;

      if (fromGroupIndex !== toGroupIndex) {
        const updatedGroups = groups.map((g, idx) => {
          if (idx === fromGroupIndex) {
            return { ...g, indices: g.indices.filter((i) => i !== modelIndex) };
          }
          if (idx === toGroupIndex) {
            return { ...g, indices: [...g.indices, modelIndex] };
          }
          return g;
        });
        setGroups(updatedGroups);
      }
    }

    setDraggedItem(null);
    setTouchStartPos(null);
  };

  // Click-to-transfer handlers
  const handleModelClick = (e, modelIndex, groupIndex) => {
    // Prevent if it's a drag operation (touchmove happened)
    if (draggedItem && touchStartPos) {
      return;
    }

    // Toggle selection
    if (
      selectedModel &&
      selectedModel.modelIndex === modelIndex &&
      selectedModel.fromGroupIndex === groupIndex
    ) {
      // Deselect if clicking the same model
      setSelectedModel(null);
    } else {
      // Select this model
      setSelectedModel({ modelIndex, fromGroupIndex: groupIndex });
    }
  };

  const handleGroupClick = (e, toGroupIndex) => {
    // Only proceed if a model is selected
    if (!selectedModel) return;

    const { modelIndex, fromGroupIndex } = selectedModel;

    // Don't do anything if clicking the same group the model is already in
    if (fromGroupIndex === toGroupIndex) {
      setSelectedModel(null);
      return;
    }

    // Transfer model to the clicked group
    const updatedGroups = groups.map((g, idx) => {
      if (idx === fromGroupIndex) {
        return { ...g, indices: g.indices.filter((i) => i !== modelIndex) };
      }
      if (idx === toGroupIndex) {
        return { ...g, indices: [...g.indices, modelIndex] };
      }
      return g;
    });

    setGroups(updatedGroups);
    setSelectedModel(null); // Deselect after transfer
  };

  const handleSave = () => {
    // Convert groups to the format expected (exclude ungrouped and empty groups)
    const savedGroups = groups
      .filter((g) => !g.isUngrouped && g.indices && g.indices.length > 0)
      .map((g) => ({
        name: g.name.trim() || "Unnamed Group", // Ensure group has a name
        // Filter out any invalid indices (edge case: model deleted after grouping)
        indices: g.indices.filter((idx) => idx >= 0 && idx < modelInfos.length),
        color: g.color,
      }))
      .filter((g) => g.indices.length > 0); // Remove groups that became empty after filtering

    // When editing, preserve the target (personal or common)
    // When creating new, use the checkbox value
    const shouldApplyToAll =
      editMode === "common"
        ? true
        : editMode === "personal"
        ? false
        : applyToAllUsers;

    onSave(savedGroups, shouldApplyToAll);
  };

  // Check if user can apply to all users
  const canApplyToAllUsers = [
    Roles.EDITOR,
    Roles.CREATOR,
    Roles.OWNER,
    Roles.ADMIN,
  ].includes(userRole);

  // Determine modal title and description based on edit mode
  const getModalTitle = () => {
    if (editMode === "personal") return "Edit My Group";
    if (editMode === "common") return "Edit Public Group";
    return "Group Segmented Structures";
  };

  const getModalDescription = () => {
    if (editMode === "personal")
      return "Edit your personal grouping. This will only affect your view.";
    if (editMode === "common")
      return "Edit the public grouping. This will affect all users.";
    return "Drag and drop models into groups, or click a model then click a group to move it. Saving will create or override your grouping.";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-[var(--main-color)]">
            {getModalTitle()}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{getModalDescription()}</p>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-x-auto overflow-y-auto p-6">
          <div className="flex gap-4 min-w-max items-start">
            {groups.map((group, groupIndex) => (
              <div
                key={group.id}
                className={`flex-shrink-0 w-64 bg-gray-50 rounded-lg border-2 flex flex-col ${
                  selectedModel
                    ? "border-blue-400 cursor-pointer hover:bg-blue-50"
                    : "border-gray-200"
                }`}
                data-group-drop-zone
                data-group-index={groupIndex}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, groupIndex)}
                onClick={(e) => {
                  // Only handle click if it's directly on the group container or drop zone
                  if (
                    e.target.hasAttribute("data-group-drop-zone") ||
                    e.target.closest("[data-group-drop-zone]") ===
                      e.currentTarget
                  ) {
                    handleGroupClick(e, groupIndex);
                  }
                }}
              >
                {/* Group Header */}
                <div className="p-3 border-b border-gray-200 bg-white rounded-t-lg">
                  {group.isUngrouped ? (
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-700">
                        {group.name}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {group.indices.length} models
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <input
                          type="text"
                          value={group.name}
                          onChange={(e) =>
                            updateGroupName(groupIndex, e.target.value)
                          }
                          className="flex-1 px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                          placeholder="Group name"
                        />
                        <button
                          onClick={() => deleteGroup(groupIndex)}
                          className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Delete group"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">Color:</span>
                          <input
                            type="color"
                            value={group.color}
                            onChange={(e) =>
                              updateGroupColor(groupIndex, e.target.value)
                            }
                            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {group.indices.length} models
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Models in this group */}
                <div
                  className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]"
                  onClick={(e) => {
                    // Allow clicking empty area to transfer selected model
                    if (
                      e.target === e.currentTarget ||
                      e.target.closest(".empty-drop-zone")
                    ) {
                      handleGroupClick(e, groupIndex);
                    }
                  }}
                >
                  {group.indices.length === 0 ? (
                    <div className="empty-drop-zone text-center text-gray-400 text-sm py-8">
                      {selectedModel
                        ? "Click to move model here"
                        : "Drop models here"}
                    </div>
                  ) : (
                    group.indices.map((modelIndex) => {
                      const model = modelInfos[modelIndex];
                      if (!model) return null;

                      const isSelected =
                        selectedModel &&
                        selectedModel.modelIndex === modelIndex &&
                        selectedModel.fromGroupIndex === groupIndex;

                      return (
                        <div
                          key={modelIndex}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, modelIndex, groupIndex)
                          }
                          onTouchStart={(e) =>
                            handleTouchStart(e, modelIndex, groupIndex)
                          }
                          onTouchMove={handleTouchMove}
                          onTouchEnd={(e) => handleTouchEnd(e, groupIndex)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleModelClick(e, modelIndex, groupIndex);
                          }}
                          className={`bg-white border rounded-lg p-3 cursor-pointer hover:shadow-md transition-all touch-none ${
                            isSelected
                              ? "border-blue-500 ring-2 ring-blue-300 shadow-md"
                              : "border-gray-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <DragIndicatorIcon
                              fontSize="small"
                              className="text-gray-400 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">
                                {model.name}
                              </p>
                            </div>
                            {isSelected && (
                              <span className="text-blue-500 text-xs font-semibold">
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}

            {/* Add New Group Button */}
            <div className="flex-shrink-0 w-64 self-stretch">
              <button
                onClick={addNewGroup}
                className="w-full h-auto min-h-[200px] border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-start pt-8 gap-2 text-gray-500 hover:border-[var(--main-color)] hover:text-[var(--main-color)] transition-colors"
              >
                <AddIcon fontSize="large" />
                <span className="font-medium">Add New Group</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex flex-col gap-3">
            {/* Checkbox/Message Section */}
            {editMode ? (
              // When editing, show info message
              <div className="text-sm text-gray-600">
                {editMode === "personal"
                  ? "Editing your personal grouping."
                  : "Editing the public grouping for all users."}
              </div>
            ) : canApplyToAllUsers ? (
              // When creating new and user has permission
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToAllUsers}
                  onChange={(e) => setApplyToAllUsers(e.target.checked)}
                  className="w-4 h-4 text-[var(--main-color)] border-gray-300 rounded focus:ring-[var(--main-color)]"
                />
                <span className="text-sm text-gray-700">
                  Apply grouping to all users (default: only for me)
                </span>
              </label>
            ) : (
              // When creating new but user doesn't have permission
              <div className="text-sm text-gray-600">
                This grouping will be saved for your personal view only.
              </div>
            )}

            {/* Buttons Section */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 transition"
              >
                Close
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-[var(--main-color)] text-white px-6 py-2 rounded hover:bg-[var(--main-color-bold)] focus:outline-none focus:ring-2 focus:ring-[var(--main-color)] transition"
              >
                Save Groups
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelGroupingModal;
